import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useLanguage } from '../../context/LanguageContext';
import { 
  ArrowLeft, Crown, FileQuestion, Users, Bell, BarChart3, 
  BookMarked, BookOpen, Landmark, FlaskConical, Languages, Trophy, Clock, School
} from 'lucide-react';

import { API_URL } from '../../lib/api';

function AdminDashboard() {
  const [reports, setReports] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { language, t } = useLanguage();

  useEffect(() => {
    fetchReports();
    fetchMe();
  }, []);

  const fetchMe = async () => {
    const token = localStorage.getItem('token');
    try {
      const r = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMe(r.data);
    } catch (e) {}
  };

  const fetchReports = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API_URL}/api/admin/reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(response.data);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const countLabel = (count, singular, plural, singularZh, pluralZh = singularZh) => {
    const value = Number(count || 0);
    if (language === 'zh') {
      return `${value} ${value === 1 ? singularZh : pluralZh}`;
    }
    return `${value} ${value === 1 ? singular : plural}`;
  };

  const cardColors = {
    violet: {
      hover: 'hover:border-violet-300',
      iconBg: 'bg-violet-100',
      iconText: 'text-violet-500',
      statBg: 'bg-violet-50',
      statText: 'text-violet-600'
    },
    blue: {
      hover: 'hover:border-blue-300',
      iconBg: 'bg-blue-100',
      iconText: 'text-blue-500',
      statBg: 'bg-blue-50',
      statText: 'text-blue-600'
    },
    cyan: {
      hover: 'hover:border-cyan-300',
      iconBg: 'bg-cyan-100',
      iconText: 'text-cyan-500',
      statBg: 'bg-cyan-50',
      statText: 'text-cyan-600'
    },
    indigo: {
      hover: 'hover:border-indigo-300',
      iconBg: 'bg-indigo-100',
      iconText: 'text-indigo-500',
      statBg: 'bg-indigo-50',
      statText: 'text-indigo-600'
    },
    pink: {
      hover: 'hover:border-pink-300',
      iconBg: 'bg-pink-100',
      iconText: 'text-pink-500',
      statBg: 'bg-pink-50',
      statText: 'text-pink-600'
    },
    green: {
      hover: 'hover:border-green-300',
      iconBg: 'bg-green-100',
      iconText: 'text-green-500',
      statBg: 'bg-green-50',
      statText: 'text-green-600'
    },
    yellow: {
      hover: 'hover:border-yellow-300',
      iconBg: 'bg-yellow-100',
      iconText: 'text-yellow-500',
      statBg: 'bg-yellow-50',
      statText: 'text-yellow-700'
    },
    rose: {
      hover: 'hover:border-rose-300',
      iconBg: 'bg-rose-100',
      iconText: 'text-rose-500',
      statBg: 'bg-rose-50',
      statText: 'text-rose-600'
    }
  };

  const menuItems = [
    { 
      icon: FileQuestion, 
      title: language === 'zh' ? '\u7ba1\u7406\u9898\u76ee' : 'Manage Questions',
      desc: language === 'zh' ? '\u6dfb\u52a0\u3001\u7f16\u8f91\u6216\u5220\u9664\u6d4b\u9a8c\u9898\u76ee' : 'Add, edit or delete quiz questions',
      path: '/admin/questions',
      color: 'violet',
      stat: countLabel(reports?.total_questions, 'question', 'questions', '\u9898')
    },
    { 
      icon: Users, 
      title: language === 'zh' ? '\u7ba1\u7406\u7528\u6237' : 'Manage Users',
      desc: language === 'zh' ? '\u67e5\u770b\u548c\u7ba1\u7406\u7528\u6237\u8d26\u6237' : 'View and manage user accounts',
      path: '/admin/users',
      color: 'blue',
      stat: countLabel(reports?.total_users, 'user', 'users', '\u7528\u6237')
    },
    { 
      icon: School, 
      title: language === 'zh' ? '\u7ba1\u7406\u5b66\u6821' : 'Manage Schools',
      desc: language === 'zh' ? '\u7ba1\u7406\u5b66\u6821\u8d44\u6599\u3001\u5fbd\u6807\u548c\u7ec4\u7ec7\u67b6\u6784' : 'Manage school profiles, logos and structure',
      path: '/admin/schools',
      color: 'cyan',
      stat: countLabel(reports?.total_schools, 'school', 'schools', '\u5b66\u6821')
    },
    {
      icon: BookMarked,
      title: language === 'zh' ? '\u7ba1\u7406\u79d1\u76ee' : 'Manage Subjects',
      desc: language === 'zh' ? '\u6309\u5b66\u6821\u3001\u5e74\u7ea7\u548c\u7ae0\u8282\u7ba1\u7406\u79d1\u76ee' : 'Manage subjects by school, form and chapter',
      path: '/admin/subjects',
      color: 'indigo',
      stat: countLabel(reports?.total_subjects, 'subject', 'subjects', '\u79d1\u76ee')
    },
    {
      icon: Languages,
      title: language === 'zh' ? '\u5207\u6362\u8bed\u8a00' : 'Switch Language',
      desc: language === 'zh' ? '管理 English = Chinese 的文字对照' : 'Manage English = Chinese text pairs',
      path: '/admin/translations',
      color: 'rose',
      stat: countLabel(reports?.total_translations, 'pair', 'pairs', '组')
    },
    { 
      icon: Bell, 
      title: language === 'zh' ? '\u7ba1\u7406\u901a\u77e5' : 'Manage Notices',
      desc: language === 'zh' ? '\u521b\u5efa\u516c\u544a\u548c\u901a\u77e5' : 'Create announcements and notices',
      path: '/admin/notices',
      color: 'pink',
      stat: countLabel(reports?.active_notices, 'active', 'active', '\u6709\u6548')
    },
    { 
      icon: BarChart3, 
      title: language === 'zh' ? '\u62a5\u544a' : 'Reports',
      desc: language === 'zh' ? '\u67e5\u770b\u8be6\u7ec6\u5206\u6790' : 'View detailed analytics',
      path: '/admin/reports',
      color: 'green',
      stat: countLabel(reports?.total_quizzes_completed, 'quiz', 'quizzes', '\u6d4b\u9a8c')
    },
    { 
      icon: Trophy, 
      title: language === 'zh' ? '\u9526\u6807\u8d5b' : 'Tournaments',
      desc: language === 'zh' ? '\u5b89\u6392\u5b9e\u65f6\u9526\u6807\u8d5b' : 'Schedule live tournaments',
      path: '/admin/tournaments',
      color: 'yellow',
      stat: countLabel(reports?.total_tournaments, 'event', 'events', '\u8d5b\u4e8b')
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-50 to-pink-50">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-violet-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-pink-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-gradient-to-r from-yellow-400 to-orange-500 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            {t('back')}
          </button>
          
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-4">
              <Crown className="w-12 h-12 text-white" />
              <div>
                <h1 className="text-3xl font-black text-white">{t('admin_panel')}</h1>
                <p className="text-white/80 font-medium">
                  {language === 'zh' ? '\u7ba1\u7406\u60a8\u7684\u6d4b\u9a8c\u5e94\u7528' : 'Manage your quiz application'}
                </p>
              </div>
            </div>
            {me?.previous_login_at && (
              <div
                className="flex items-center gap-1.5 text-sm md:text-base text-white/90 bg-white/15 backdrop-blur-sm px-3 py-2 rounded-xl"
                data-testid="admin-last-login-display"
              >
                <Clock className="w-4 h-4" />
                <span>
                  {language === 'zh' ? '\u4e0a\u6b21\u767b\u5f55:' : 'Last login:'}{' '}
                  <span className="font-bold">
                    {new Date(me.previous_login_at).toLocaleString(
                      language === 'zh' ? 'zh-CN' : 'en-US',
                      { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }
                    )}
                  </span>
                </span>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Menu Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {menuItems.map((item, index) => {
            const tone = cardColors[item.color];

            return (
              <motion.button
                key={item.path}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                onClick={() => navigate(item.path)}
                className={`bg-white rounded-2xl p-6 border-2 border-zinc-200 text-left ${tone.hover} hover:shadow-md transition-all group`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className={`w-12 h-12 rounded-xl ${tone.iconBg} flex items-center justify-center mb-4`}>
                      <item.icon className={`w-6 h-6 ${tone.iconText}`} />
                    </div>
                    <h3 className="text-xl font-bold text-zinc-900 mb-1">{item.title}</h3>
                    <p className="text-sm text-zinc-500">{item.desc}</p>
                  </div>
                  <div className={`shrink-0 rounded-xl ${tone.statBg} px-3 py-2 text-right`}>
                    <p className={`text-sm font-black ${tone.statText} whitespace-nowrap`}>{item.stat}</p>
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>

        {/* Subject Stats */}
        {reports?.subject_stats && (
          <div className="bg-white rounded-2xl p-6 border-2 border-zinc-200">
            <h3 className="text-xl font-bold text-zinc-900 mb-4">
              {language === 'zh' ? '\u79d1\u76ee\u7edf\u8ba1' : 'Subject Statistics'}
            </h3>
            <div className="space-y-4">
              {reports.subject_stats.map((stat) => {
                const icons = {
                  'subj_bm': BookOpen,
                  'subj_history': Landmark,
                  'subj_science': FlaskConical
                };
                const Icon = icons[stat.subject.id] || BookOpen;
                
                return (
                  <div key={stat.subject.id} className="flex items-center gap-4">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${stat.subject.color}20` }}
                    >
                      <Icon className="w-5 h-5" style={{ color: stat.subject.color }} />
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-zinc-900">
                        {language === 'zh' ? stat.subject.name_zh : stat.subject.name_en}
                      </p>
                      <p className="text-sm text-zinc-500">
                        {stat.quizzes_completed} {language === 'zh' ? '\u6b21\u6d4b\u9a8c' : 'quizzes'} -
                        {stat.average_score_pct}% {language === 'zh' ? '\u5e73\u5747\u5206' : 'avg score'}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default AdminDashboard;
